import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import { PAGE_IDS, PERMISSION_IDS } from '../../../../constants/permissions';
import { usePermissions } from '../../../../context/PermissionContext';
import { settingsService } from '../../../../services/settings.service';
import { systemService } from '../../../../services/system.service';
import type { DesignationOption, DropdownOption, UserProfileData, UserProfileSidebarData } from '../../../../services/types/userProfile.types';
import { userProfileService } from '../../../../services/userProfile.service';
import { buildDirtyProfileUpdate } from '../../../../utils/userProfileDiff';
import EmploymentInfoCard from './EmploymentInfoCard';
import ProfileInfoCard from './ProfileInfoCard';
import TeamInfoCard from './TeamInfoCard';

export { buildDirtyProfileUpdate };

function toSidebar(data: UserProfileData): UserProfileSidebarData {
  return {
    fullName: `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim(),
    designationTitle: data.designationName ?? 'N/A',
    teamName: data.functionalTeamName ?? 'N/A',
    supervisorLabel: 'N/A',
    directReportLabel: 'N/A',
  };
}

export interface ProfileInfoProps {
  setSummary: (data: UserProfileSidebarData | null) => void;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

const ProfileInfo: React.FC<ProfileInfoProps> = ({ setSummary, onNotify }) => {
  const { setPageId, hasPermission } = usePermissions();
  const formRef = useRef<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState<UserProfileData | null>(null);
  const [formData, setFormData] = useState<UserProfileData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});

  const [genders, setGenders] = useState<DropdownOption[]>([]);
  const [departments, setDepartments] = useState<DropdownOption[]>([]);
  const [designations, setDesignations] = useState<DesignationOption[]>([]);
  const [userRoles, setUserRoles] = useState<DropdownOption[]>([]);
  const [permissionLevels, setPermissionLevels] = useState<DropdownOption[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<DropdownOption[]>([]);
  const [functionalTeams, setFunctionalTeams] = useState<DropdownOption[]>([]);

  const canEdit = hasPermission(PERMISSION_IDS.USER_PROFILE_EDIT);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        profileRes,
        gRes,
        dRes,
        desRes,
        rolesRes,
        permRes,
        empRes,
        teamsRes,
      ] = await Promise.all([
        userProfileService.getProfile(),
        systemService.getGenders(),
        systemService.getDepartments(),
        systemService.getDesignations(),
        settingsService.getUserRoles(),
        settingsService.getPermissionLevels(),
        settingsService.getEmploymentTypes(),
        settingsService.getFunctionalTeams(),
      ]);

      const profile = profileRes.data.data;
      setInitialData(profile);
      setFormData(profile);
      setGenders(gRes.data.data ?? []);
      setDepartments(dRes.data.data ?? []);
      setDesignations(desRes.data.data ?? []);
      setUserRoles(rolesRes.data.data ?? []);
      setPermissionLevels(permRes.data.data ?? []);
      setEmploymentTypes(empRes.data.data ?? []);
      setFunctionalTeams(teamsRes.data.data ?? []);
    } catch {
      onNotify('Unable to load profile. Please try again.', 'error');
      setInitialData(null);
      setFormData(null);
    } finally {
      setLoading(false);
    }
  }, [onNotify]);

  useEffect(() => {
    setPageId(PAGE_IDS.USER_PROFILE);
  }, [setPageId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  formRef.current = formData;

  useEffect(() => {
    if (!formData) {
      setSummary(null);
      return;
    }
    setSummary(toSidebar(formData));
  }, [formData, setSummary]);

  const patchForm = useCallback((patch: Partial<UserProfileData>) => {
    setFormData((prev) => (prev ? { ...prev, ...patch } : prev));
    setErrors((e) => {
      const next = { ...e };
      if (patch.firstName !== undefined && String(patch.firstName).trim()) delete next.firstName;
      if (patch.lastName !== undefined && String(patch.lastName).trim()) delete next.lastName;
      return next;
    });
  }, []);

  const validateRequired = useCallback((data: UserProfileData) => {
    const next: { firstName?: string; lastName?: string } = {};
    if (!data.firstName.trim()) next.firstName = 'First name is required';
    if (!data.lastName.trim()) next.lastName = 'Last name is required';
    return next;
  }, []);

  const handleBlurName = useCallback((field: 'firstName' | 'lastName') => {
    const current = formRef.current;
    if (!current) return;
    setErrors((prev) => {
      const next = { ...prev };
      if (field === 'firstName') {
        next.firstName = !current.firstName.trim() ? 'First name is required' : undefined;
      } else {
        next.lastName = !current.lastName.trim() ? 'Last name is required' : undefined;
      }
      return next;
    });
  }, []);

  const handleDiscard = () => {
    if (initialData) setFormData(initialData);
    setIsEditMode(false);
    setErrors({});
  };

  const handleSave = async () => {
    if (!formData || !initialData) return;
    const v = validateRequired(formData);
    setErrors(v);
    if (v.firstName || v.lastName) {
      onNotify('Please fix validation errors before saving.', 'error');
      return;
    }
    const dirty = buildDirtyProfileUpdate(initialData, formData);
    if (Object.keys(dirty).length === 0) {
      onNotify('No changes to save.', 'error');
      return;
    }
    setSaving(true);
    try {
      await userProfileService.updateProfile(dirty);
      onNotify('Profile updated successfully.', 'success');
      setIsEditMode(false);
      setErrors({});
      await loadAll();
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' && e !== null && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      onNotify(msg ?? 'Unable to save profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <Loading />
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box py={4}>
        <Typography sx={{ fontSize: '15px', color: '#475467' }}>Unable to display profile.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ProfileInfoCard
        data={formData}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onChange={patchForm}
        errors={errors}
        genders={genders}
        designations={designations}
        userRoles={userRoles}
        onBlurName={handleBlurName}
      />
      <EmploymentInfoCard
        data={formData}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onChange={patchForm}
        departments={departments}
        permissionLevels={permissionLevels}
        employmentTypes={employmentTypes}
      />
      <TeamInfoCard
        data={formData}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onChange={patchForm}
        functionalTeams={functionalTeams}
      />
      {isEditMode && canEdit ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '1246px', mt: 1 }}>
          <Button
            variant="outlined"
            disabled={saving}
            className="edit-section-cancel-button"
            onClick={handleDiscard}
          >
            Discard
          </Button>
          <Button
            isLoading={saving}
            variant="contained"
            disabled={saving}
            className="edit-section-save-button"
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default ProfileInfo;
